'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import BookingSteps from './BookingSteps';
import ServiceSelection from './ServiceSelection';
import CosmicTiming from './CosmicTiming';
import PersonalDetails from './PersonalDetails';
import PaymentDetails from './PaymentDetails';
import BookingConfirmation from './BookingConfirmation';
import { createBooking, validatePromoCode } from '@/actions/booking-actions';

const services = {
  'numerology-basic': { name: 'Complete Numerology Reading', price: 149 },
  'astrology-comprehensive': { name: 'Birth Chart Analysis', price: 199 },
  'remedy-package': { name: 'Personalized Remedy Package', price: 99 },
  'career-guidance': { name: 'Career Path Guidance', price: 129 },
  'relationship-compatibility': { name: 'Relationship Compatibility', price: 179 },
  'emergency-guidance': { name: 'Emergency Cosmic Guidance', price: 89 },
  'basic-package': { name: 'Cosmic Starter', price: 249 },
  'transformation-package': { name: 'Cosmic Transformation', price: 499 },
  'mastery-package': { name: 'Cosmic Mastery', price: 799 },
};

const BookingInteractive = () => {
  const searchParams = useSearchParams();
  const initialService = searchParams.get('service');
  const initialPackage = searchParams.get('package');

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState<string | null>(initialService || initialPackage || null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Update selectedService if search parameter changes (though mainly for initial load)
  useEffect(() => {
    if (initialService && services[initialService as keyof typeof services]) {
      setSelectedService(initialService);
      setCurrentStep(2);
    }
    if (initialPackage && services[initialPackage as keyof typeof services]) {
      setSelectedService(initialPackage);
      setCurrentStep(2);
    }
  }, [initialService, initialPackage]);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    concerns: ''
  });

  // Reusing cardDetails structure for UTR for simplicity in refactor, or we can add specific field
  // Using transactionId in PaymentDetails component mapping to this state
  const [paymentDetails, setPaymentDetails] = useState({
    transactionId: '',
  });

  // Promo Code State
  const [promoCode, setPromoCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(0);
  const [isPromoValid, setIsPromoValid] = useState(false);
  const [promoError, setPromoError] = useState('');

  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState({
    confirmationNumber: '',
    serviceName: '',
    date: '',
    time: '',
    customerName: '',
    customerEmail: '',
    status: 'pending'
  });

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePaymentDetailsChange = (field: string, value: string) => {
    setPaymentDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyPromo = async () => {
    if (!promoCode) return;
    setPromoError('');

    // Basic local check for example, but using server action preferred
    const result = await validatePromoCode(promoCode);

    if (result.success) {
      setIsPromoValid(true);
      const servicePrice = selectedService ? services[selectedService as keyof typeof services].price : 0;

      let discount = 0;
      if (result.discountType === 'percentage') {
        discount = servicePrice * (result.discountValue / 100);
      } else {
        discount = result.discountValue;
      }
      setDiscountApplied(Math.min(discount, servicePrice)); // Cannot exceed price
    } else {
      setIsPromoValid(false);
      setDiscountApplied(0);
      setPromoError(result.message || 'Invalid promo code');
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedService !== null;
      case 2:
        return selectedDate !== null && selectedTime !== null;
      case 3:
        return (
          formData.fullName.trim() !== '' &&
          formData.email.trim() !== '' &&
          formData.phone.trim() !== '' &&
          formData.dateOfBirth.trim() !== '' &&
          formData.placeOfBirth.trim() !== ''
        );
      case 4:
        // Validate UTR is present
        return paymentDetails.transactionId.trim().length >= 4; // Basic length check
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (canProceedToNextStep()) {
      if (currentStep === 4) {
        setIsSubmitting(true);
        const serviceIdx = selectedService as keyof typeof services;
        const finalAmount = services[serviceIdx].price - discountApplied;

        // Persist to DB
        const result = await createBooking({
          serviceId: selectedService!,
          serviceName: services[serviceIdx].name,
          date: selectedDate!,
          time: selectedTime!,
          customerName: formData.fullName,
          customerEmail: formData.email,
          customerPhone: formData.phone,
          dateOfBirth: formData.dateOfBirth,
          timeOfBirth: formData.timeOfBirth,
          placeOfBirth: formData.placeOfBirth,
          concerns: formData.concerns,
          paymentMethod: 'upi',
          transactionId: paymentDetails.transactionId,
          amount: finalAmount
        });

        if (result.success) {
          const dateObj = new Date(selectedDate!);
          const formattedDate = dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });

          setConfirmationDetails({
            status: 'pending',
            confirmationNumber: result.confirmationNumber || 'PENDING',
            serviceName: services[serviceIdx].name,
            date: formattedDate,
            time: selectedTime || '',
            customerName: formData.fullName,
            customerEmail: formData.email
          });
          setBookingConfirmed(true);
        } else {
          alert('Booking failed. Please try again. ' + (result.message || ''));
        }
        setIsSubmitting(false);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (bookingConfirmed) {
    return <BookingConfirmation data-component-id="src/app/booking/components/BookingInteractive.tsx:198:11" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="198" data-component-end-line="198" data-component-file="BookingInteractive.tsx" data-component-name="BookingConfirmation" data-component-content="%7B%22elementName%22%3A%22BookingConfirmation%22%7D" bookingDetails={confirmationDetails} />;
  }

  return (
    <div data-component-id="src/app/booking/components/BookingInteractive.tsx:202:4" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="202" data-component-end-line="284" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      <BookingSteps data-component-id="src/app/booking/components/BookingInteractive.tsx:203:6" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="203" data-component-end-line="203" data-component-file="BookingInteractive.tsx" data-component-name="BookingSteps" data-component-content="%7B%22elementName%22%3A%22BookingSteps%22%7D" currentStep={currentStep} />

      <div data-component-id="src/app/booking/components/BookingInteractive.tsx:205:6" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="205" data-component-end-line="244" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-%5B500px%5D%22%7D" className="min-h-[500px]">
        {currentStep === 1 && (
          <ServiceSelection data-component-id="src/app/booking/components/BookingInteractive.tsx:207:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="207" data-component-end-line="210" data-component-file="BookingInteractive.tsx" data-component-name="ServiceSelection" data-component-content="%7B%22elementName%22%3A%22ServiceSelection%22%7D"
            selectedService={selectedService}
            onServiceSelect={setSelectedService}
          />
        )}

        {currentStep === 2 && (
          <CosmicTiming data-component-id="src/app/booking/components/BookingInteractive.tsx:214:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="214" data-component-end-line="219" data-component-file="BookingInteractive.tsx" data-component-name="CosmicTiming" data-component-content="%7B%22elementName%22%3A%22CosmicTiming%22%7D"
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateSelect={setSelectedDate}
            onTimeSelect={setSelectedTime}
          />
        )}

        {currentStep === 3 && (
          <PersonalDetails data-component-id="src/app/booking/components/BookingInteractive.tsx:223:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="223" data-component-end-line="226" data-component-file="BookingInteractive.tsx" data-component-name="PersonalDetails" data-component-content="%7B%22elementName%22%3A%22PersonalDetails%22%7D"
            formData={formData}
            onFormChange={handleFormChange}
          />
        )}

        {currentStep === 4 && selectedService && (
          <PaymentDetails data-component-id="src/app/booking/components/BookingInteractive.tsx:230:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="230" data-component-end-line="242" data-component-file="BookingInteractive.tsx" data-component-name="PaymentDetails" data-component-content="%7B%22elementName%22%3A%22PaymentDetails%22%7D"
            servicePrice={services[selectedService as keyof typeof services].price}
            paymentMethod="upi"
            onPaymentMethodChange={() => { }}
            cardDetails={paymentDetails} // Reusing prop name for minimal refactor of other components if needed, but we updated PaymentDetails to just accept "cardDetails" as a bag
            onCardDetailsChange={handlePaymentDetailsChange}
            promoCode={promoCode}
            onPromoCodeChange={setPromoCode}
            onApplyPromo={handleApplyPromo}
            discountApplied={discountApplied}
            promoError={promoError}
            isPromoValid={isPromoValid}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div data-component-id="src/app/booking/components/BookingInteractive.tsx:247:6" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="247" data-component-end-line="283" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20pt-6%20border-t%20border-border%22%7D" className="flex items-center justify-between pt-6 border-t border-border">
        {currentStep === 1 && initialService ? (
          // If pre-selected (e.g. from services page), maybe don't show back button or handle it differently?
          // Actually, standard behavior is fine.
          <button data-component-id="src/app/booking/components/BookingInteractive.tsx:251:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="251" data-component-end-line="257" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20bg-muted%20text-foreground%20rounded-lg%20font-cta%20font-semibold%20border-2%20border-border%20hover%3Abg-secondary%20transition-all%20duration-300%20flex%20items-center%20space-x-2%22%7D"
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-muted text-foreground rounded-lg font-cta font-semibold border-2 border-border hover:bg-secondary transition-all duration-300 flex items-center space-x-2"
          >
            <Icon data-component-id="src/app/booking/components/BookingInteractive.tsx:255:12" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="255" data-component-end-line="255" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowLeftIcon%22%7D" name="ArrowLeftIcon" size={20} />
            <span data-component-id="src/app/booking/components/BookingInteractive.tsx:256:12" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="256" data-component-end-line="256" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Back%20to%20Services%22%7D">Back to Services</span>
          </button>
        ) : (
          <button data-component-id="src/app/booking/components/BookingInteractive.tsx:259:10" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="259" data-component-end-line="266" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20bg-muted%20text-foreground%20rounded-lg%20font-cta%20font-semibold%20border-2%20border-border%20hover%3Abg-secondary%20transition-all%20duration-300%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20flex%20items-center%20space-x-2%22%7D"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-6 py-3 bg-muted text-foreground rounded-lg font-cta font-semibold border-2 border-border hover:bg-secondary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Icon data-component-id="src/app/booking/components/BookingInteractive.tsx:264:12" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="264" data-component-end-line="264" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowLeftIcon%22%7D" name="ArrowLeftIcon" size={20} />
            <span data-component-id="src/app/booking/components/BookingInteractive.tsx:265:12" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="265" data-component-end-line="265" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Back%22%7D">Back</span>
          </button>
        )}

        <button data-component-id="src/app/booking/components/BookingInteractive.tsx:269:8" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="269" data-component-end-line="282" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-8%20py-3%20bg-accent%20text-accent-foreground%20rounded-lg%20font-cta%20font-semibold%20shadow-golden%20hover%3Ashadow-golden-lg%20transition-all%20duration-300%20hover%3Ascale-105%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20disabled%3Ahover%3Ascale-100%20flex%20items-center%20space-x-2%22%7D"
          onClick={handleNext}
          disabled={!canProceedToNextStep() || isSubmitting}
          className="px-8 py-3 bg-accent text-accent-foreground rounded-lg font-cta font-semibold shadow-golden hover:shadow-golden-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center space-x-2"
        >
          {isSubmitting ? (
            <span data-component-id="src/app/booking/components/BookingInteractive.tsx:275:12" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="275" data-component-end-line="275" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Processing...%22%7D">Processing...</span>
          ) : (
            <>
              <span data-component-id="src/app/booking/components/BookingInteractive.tsx:278:14" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="278" data-component-end-line="278" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{currentStep === 4 ? 'Complete Booking' : 'Continue'}</span>
              <Icon data-component-id="src/app/booking/components/BookingInteractive.tsx:279:14" data-component-path="src/app/booking/components/BookingInteractive.tsx" data-component-line="279" data-component-end-line="279" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={currentStep === 4 ? 'CheckIcon' : 'ArrowRightIcon'} size={20} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BookingInteractive;